import React, { useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useTheme, ThemeColors } from '../../context/ThemeContext';
import { AttendanceMonthDay } from '../../types';
import {
  ATTENDANCE_STATUS_COLOR,
  dayKeyOf,
  formatDayShort,
  sessionsLine,
} from '../../utils/attendanceSessions';
import { formatLocalTime } from '../../utils/dateUtils';

interface Props {
  /** Days to list, already in display order (newest first). */
  days: AttendanceMonthDay[];
  /** Highlighted row, "2026-10-09". */
  selectedKey?: string | null;
  onSelect?: (key: string) => void;
  emptyText?: string;
}

/**
 * Past days, newest first: date, overall status and - for a school taking two
 * registers - which register each status came from.
 */
export function AttendanceHistoryList({ days, selectedKey, onSelect, emptyText = 'No attendance recorded yet.' }: Props) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);

  if (days.length === 0) {
    return <Text style={styles.emptyText}>{emptyText}</Text>;
  }

  return (
    <View>
      {days.map((day, index) => {
        const key = dayKeyOf(day.date);
        const color = ATTENDANCE_STATUS_COLOR[day.status];
        const line =
          sessionsLine(day) ||
          (day.status === 'Late' && day.arrivalTime ? `Arrived ${formatLocalTime(day.arrivalTime)}` : '');
        const isSelected = key === selectedKey;

        return (
          <TouchableOpacity
            key={key}
            activeOpacity={0.7}
            disabled={!onSelect}
            onPress={() => onSelect?.(key)}
            style={[
              styles.row,
              index < days.length - 1 && styles.rowDivider,
              isSelected && { backgroundColor: `${colors.primary}12` },
            ]}
            accessibilityRole="button"
            accessibilityLabel={`${formatDayShort(key)}: ${day.status}${line ? `, ${line}` : ''}`}
          >
            <View style={[styles.statusBar, { backgroundColor: color }]} />
            <View style={styles.textWrap}>
              <Text style={styles.dateText}>{formatDayShort(key)}</Text>
              {line ? <Text style={styles.lineText}>{line}</Text> : null}
            </View>
            <View style={[styles.pill, { backgroundColor: `${color}1A` }]}>
              <Text style={[styles.pillText, { color }]}>{day.status}</Text>
            </View>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 12,
      paddingHorizontal: 8,
      borderRadius: 10,
    },
    rowDivider: {
      borderBottomWidth: 1,
      borderBottomColor: colors.divider,
    },
    statusBar: {
      width: 4,
      alignSelf: 'stretch',
      borderRadius: 2,
      marginRight: 12,
    },
    textWrap: {
      flex: 1,
    },
    dateText: {
      fontSize: 14,
      fontWeight: '700',
      color: colors.textPrimary,
    },
    lineText: {
      fontSize: 12,
      color: colors.textSecondary,
      fontWeight: '500',
      marginTop: 3,
    },
    pill: {
      borderRadius: 999,
      paddingVertical: 4,
      paddingHorizontal: 10,
      marginLeft: 10,
    },
    pillText: {
      fontSize: 12,
      fontWeight: '800',
    },
    emptyText: {
      fontSize: 14,
      color: colors.textMuted,
      fontWeight: '500',
      paddingVertical: 12,
      textAlign: 'center',
    },
  });
